import { Alpine } from '../../core/alpine-core.js'
import { entityDocId, entityRefId, unwrapApiEntity } from '../../core/api-entity.js'
import { apiFetch } from '../api.js'
const PLAYER_ASSIGN_SELECT = '_id,fullName,nickname,imageUrl,registeredTeamId,status'

/** Module-level helpers — safe for Alpine :attr bindings and nested scopes. */
function teamEditPlayerFullName(p) {
    if (!p) return 'Player'
    return String(p.fullName || p.displayName || 'Player').trim() || 'Player'
}

function teamEditPlayerNickname(p) {
    return String(p?.nickname || '').trim()
}

function teamEditPlayerInitials(p) {
    const name = teamEditPlayerFullName(p)
    if (!name) return 'P'
    return name.split(/\s+/).slice(0, 2).map((part) => part[0]?.toUpperCase() || '').join('') || 'P'
}

function teamEditPlayerAvatarUrl(p) {
    const url = String(p?.imageUrl || '').trim()
    return url || null
}

function teamEditPlayerStatusLabel(status) {
    const s = String(status || 'active').toLowerCase()
    if (s === 'inactive') return 'Inactive'
    if (s === 'reserve') return 'Reserve'
    if (s === 'pool') return 'Pool'
    return 'Match squad'
}

function teamEditPlayerStatusBadgeClass(status) {
    const s = String(status || 'active').toLowerCase()
    if (s === 'inactive') return 'border-neutral-500/30 bg-neutral-500/10 text-neutral-400'
    if (s === 'reserve') return 'border-violet-400/30 bg-violet-500/10 text-violet-300'
    return 'border-emerald-400/30 bg-emerald-500/10 text-emerald-300'
}

/** Pinecone + :attr bindings resolve globals reliably — not always component methods. */
globalThis.teamEditPlayerFullName = teamEditPlayerFullName
globalThis.teamEditPlayerNickname = teamEditPlayerNickname
globalThis.teamEditPlayerInitials = teamEditPlayerInitials
globalThis.teamEditPlayerAvatarUrl = teamEditPlayerAvatarUrl
globalThis.teamEditPlayerStatusLabel = teamEditPlayerStatusLabel
globalThis.teamEditPlayerStatusBadgeClass = teamEditPlayerStatusBadgeClass

Alpine.data('teamsEdit', () => ({

    tab: 'basics',
    isEditing: false,
    saving: { basics: false },

    /** Player ids ticked in add-player search (multi-add). */
    builderSearchSelected: {},

    form: {
        _id: null,
        name: '',
        active: true,
        photoUrl: '',
        logoUrl: '',
        captainPlayerId: '',
        league: '',
        venueId: '',
    },

    roster: [],           // match squad (status active / not reserve+inactive)
    inactivePlayers: [],  // on team but not selectable for lineups
    reserves: [],         // members with status === 'reserve'
    venues: [],
    venueSearch: { q: '' },
    showAllVenues: false,
    venuePickerLimit: 10,
    teamPlayerIds: new Set(),
    teamId: null,

    loading: true,

    busy: {
        search: false,
        batchAssign: false,
        captain: {},   // { [playerId]: true }
        move: {},      // { [playerId]: true }
        remove: {},    // { [playerId]: true }
        assign: {},    // { [playerId]: true }
    },

    removeOpen: false,
    removeBusy: false,
    toRemovePlayer: null,

    deleteOpen: false,
    deleteBusy: false,

    setBusy(action, id, on) { this.busy[action][this.id(id)] = !!on },
    isBusy(action, p) { return !!this.busy[action][this.id(p?._id)] },

    cloneSpinner(el) {
        // cloneNode(true) works on <template>. Use .content for its fragment
        el.append(this.$refs.spinnerTpl.content.cloneNode(true))
    },

    id(v) { return v == null ? '' : String(v) },
    isCaptain(p) { return this.id(p._id) === this.id(this.form.captainPlayerId) },
    search: { q: '', open: false, results: [] },

    readRouteId() {
        const url = new URL(location.href)
        const idFromQuery = url.searchParams.get('id')
        if (idFromQuery) return idFromQuery

        const path = (url.hash && url.hash.startsWith('#/')) ? url.hash.slice(1) : url.pathname
        const parts = path.split('/').filter(Boolean)
        const idx = parts.indexOf('teams')
        if (idx >= 0 && parts[idx + 1]) {
            const seg = decodeURIComponent(parts[idx + 1])
            return seg === 'new' ? null : seg
        }
        return null
    },

    viewUrl() {
        if (!this.form._id) return '/teams'
        return `/admin/teams/${encodeURIComponent(this.form._id)}`
    },

    playerDisplayName: teamEditPlayerFullName,
    playerFullName: teamEditPlayerFullName,
    playerNickname: teamEditPlayerNickname,
    playerInitials: teamEditPlayerInitials,
    playerAvatarUrl: teamEditPlayerAvatarUrl,

    playerLabel(p) {
        if (!p) return 'Player'
        const name = teamEditPlayerFullName(p)
        const nick = teamEditPlayerNickname(p)
        if (!nick) return name
        return `${name} (${nick})`
    },

    playerStatusKey(p) {
        return String(p?.status || 'active').toLowerCase()
    },

    playerStatusLabel: teamEditPlayerStatusLabel,
    playerStatusBadgeClass: teamEditPlayerStatusBadgeClass,

    playersSummaryText() {
        const parts = []
        if (this.roster.length) parts.push(`${this.roster.length} match`)
        if (this.inactivePlayers.length) parts.push(`${this.inactivePlayers.length} inactive`)
        if (this.reserves.length) parts.push(`${this.reserves.length} reserve`)
        return parts.length ? parts.join(' · ') : 'No players yet'
    },

    venuesPickerList() {
        const q = String(this.venueSearch?.q || '').trim().toLowerCase()
        const list = Array.isArray(this.venues) ? [...this.venues] : []
        if (q) {
            return list.filter((v) => String(v.name || '').toLowerCase().includes(q))
        }
        const limit = this.venuePickerLimit || 10
        if (this.showAllVenues) return list
        let slice = list.slice(0, limit)
        const selectedId = String(this.form?.venueId || '').trim()
        if (selectedId && !slice.some((v) => String(v._id) === selectedId)) {
            const selected = list.find((v) => String(v._id) === selectedId)
            if (selected) slice = [selected, ...slice.slice(0, Math.max(0, limit - 1))]
        }
        return slice
    },

    venuesPickerHint() {
        const total = Array.isArray(this.venues) ? this.venues.length : 0
        const q = String(this.venueSearch?.q || '').trim()
        const shown = this.venuesPickerList().length
        if (!total) return 'No venues loaded'
        if (q) return shown ? `${shown} match${shown === 1 ? '' : 'es'}` : 'No venues match your search'
        if (total <= (this.venuePickerLimit || 10)) return `${total} venue${total === 1 ? '' : 's'}`
        if (this.showAllVenues) return `Showing all ${total} venues`
        return `Showing ${shown} of ${total} — search or show all`
    },

    canShowMoreVenues() {
        const total = Array.isArray(this.venues) ? this.venues.length : 0
        const q = String(this.venueSearch?.q || '').trim()
        return !q && !this.showAllVenues && total > (this.venuePickerLimit || 10)
    },

    selectedVenueName() {
        if (!this.form.venueId) return 'No home venue selected'
        const v = this.venues.find((x) => String(x._id) === String(this.form.venueId))
        return v?.name || 'Unknown venue'
    },

    clearVenueSearch() {
        this.venueSearch.q = ''
        this.showAllVenues = false
    },

    selectVenue(id) {
        this.form.venueId = this.id(id)
        this.venueSearch.q = ''
    },

    assignHint(p) {
        if (this.teamPlayerIds.has(p._id)) return 'Already in this team'
        if (p.registeredTeamId && p.registeredTeamId !== this.teamId) return 'Assigned to another team'
        return 'Available'
    },

    canAssign(p) {
        if (!p || !p._id) return false
        if (this.teamPlayerIds.has(p._id)) return false
        if (p.registeredTeamId && p.registeredTeamId !== this.teamId) return false
        return true
    },

    canSelectPlayerInSearch(p) {
        return this.canAssign(p)
    },

    searchResultsForAssign() {
        return (this.search.results || []).filter((p) => this.canAssign(p))
    },

    assignableSearchCount() {
        return this.searchResultsForAssign().length
    },

    pageTitle() {
        return this.isEditing ? 'Edit team' : 'Add team'
    },

    playerAssignPickerLabel() {
        if (!this.isEditing) return 'Save team basics first'
        if (this.busy.search) return 'Loading players…'
        const n = this.searchResultsForAssign().length
        if (this.search.open && n) return `${n} player${n === 1 ? '' : 's'} — search or pick below`
        return 'Search and add players from your pool…'
    },

    async openPlayerAssignPicker() {
        if (!this.isEditing) {
            this.$store?.toastr?.info?.('Create the team on the Basics tab first.')
            this.tab = 'basics'
            return
        }
        this.search.open = true
        if (!String(this.search.q || '').trim()) {
            await this.loadPlayerAssignPool()
        }
        Alpine.nextTick(() => this.$refs?.playerAssignSearchInput?.focus?.())
    },

    isBuilderSearchSelected(p) {
        if (!p?._id) return false
        return !!this.builderSearchSelected[this.id(p._id)]
    },

    toggleBuilderSearchSelected(p) {
        if (!this.canSelectPlayerInSearch(p)) return
        const pid = this.id(p._id)
        if (this.builderSearchSelected[pid]) {
            const next = { ...this.builderSearchSelected }
            delete next[pid]
            this.builderSearchSelected = next
        } else {
            this.builderSearchSelected = { ...this.builderSearchSelected, [pid]: true }
        }
    },

    builderSearchSelectedCount() {
        return Object.keys(this.builderSearchSelected || {}).filter(Boolean).length
    },

    clearBuilderSearchSelection() {
        this.builderSearchSelected = {}
    },

    selectAllBuilderSearchResults() {
        const next = { ...this.builderSearchSelected }
        for (const p of this.searchResultsForAssign()) {
            if (!this.canSelectPlayerInSearch(p)) continue
            next[this.id(p._id)] = true
        }
        this.builderSearchSelected = next
    },

    async assignSelectedPlayers(kind = 'roster') {
        const selected = (this.search.results || []).filter(
            (p) => this.builderSearchSelected[this.id(p._id)] && this.canAssign(p),
        )
        if (!selected.length) {
            this.$store.toastr.warning('Select one or more players first')
            return
        }
        this.busy.batchAssign = true
        let added = 0
        try {
            for (const p of selected) {
                const ok = await this.assignPlayer(p, kind, { silent: true, reload: false })
                if (ok) added += 1
            }
            await this.loadRoster(this.teamId)
            this.clearBuilderSearchSelection()
            if (added) {
                const role =
                    kind === 'reserve' ? 'reserve' : kind === 'inactive' ? 'inactive' : 'match squad'
                this.$store.toastr.success(
                    added === 1 ? `1 player added to ${role}` : `${added} players added to ${role}`,
                )
            } else {
                this.$store.toastr.info('No players were added')
            }
            if (!(this.search.results || []).length) {
                this.search.open = !!String(this.search.q || '').trim()
            }
        } catch (e) {
            this.$store.toastr.error(e.message || 'Failed to add selected players')
        } finally {
            this.busy.batchAssign = false
        }
    },

    async init(id = null) {
        const routeId = id ?? this.readRouteId()
        try {
            const tabParam = new URL(location.href).searchParams.get('tab')
            if (tabParam === 'players' || tabParam === 'roster') this.tab = 'players'
            else if (tabParam === 'basics') this.tab = 'basics'
        } catch { /* ignore */ }
        await this.loadVenues()
        this.isEditing = Boolean(routeId)
        this.builderSearchSelected = {}
        if (this.isEditing) {
            await this.loadRoster(routeId)
            await this.loadTeam(routeId)
        } else {
            this.tab = 'basics'
        }
        this.loading = false
    },

    async reloadAll() {
        await this.loadRoster(this.teamId)
    },

    async loadTeam(id) {
        try {
            const r = await apiFetch(`/teams/${id}?populate=false`)
            const d = await r.json()
            if (!r.ok) throw new Error(d.message || 'Failed to load team')

            const t = unwrapApiEntity(d, ['team', 'item']) || d
            this.teamId = t._id
            this.form = {
                _id: t._id,
                name: t.name || '',
                active: t.active !== false,
                photoUrl: t.photoUrl || '',
                logoUrl: t.logoUrl || '',
                captainPlayerId: entityRefId(t.captainPlayerId) || entityRefId(t.captainPlayerRef),
                venueId: this.id(t.venueId || ''),
                league: t.league || ''
            }
        } catch (e) {
            this.$store.toastr.error(e.message || 'Failed to load team')
        }
    },

    async loadRoster(id) {
        try {
            const r = await apiFetch(`/teams/${id}/roster?sortBy=fullName:asc`)
            const d = await r.json()
            if (!r.ok) throw new Error(d.message || 'Failed to load roster')

            const rosterAll = Array.isArray(d.roster) ? d.roster : []
            const inactive = Array.isArray(d.inactive) ? d.inactive : []
            const reserves = Array.isArray(d.reserves) ? d.reserves : []

            const inactiveIds = new Set(inactive.map(p => this.id(p._id)))
            const reserveIds = new Set(reserves.map(p => this.id(p._id)))
            this.roster = rosterAll
                .map(p => ({ ...p, _id: this.id(p._id) }))
                .filter(p => !reserveIds.has(p._id) && !inactiveIds.has(p._id))
            this.inactivePlayers = inactive.map(p => ({ ...p, _id: this.id(p._id) }))
            this.reserves = reserves.map(p => ({ ...p, _id: this.id(p._id) }))

            this.teamPlayerIds = new Set([...this.roster, ...this.inactivePlayers, ...this.reserves].map(p => p._id))
        } catch (e) {
            this.$store.toastr.error(e.message || 'Failed to load roster')
        }
    },

    async loadVenues() {
        try {
            const r = await apiFetch(`/venues?select=_id,name&sortBy=name:asc&limit=500`)
            const d = await r.json()
            if (!r.ok) throw new Error(d.message || 'Failed to load venues')
            const items = Array.isArray(d.items) ? d.items : Array.isArray(d) ? d : []
            this.venues =
                items
                    .map(v => ({ _id: this.id(v._id), name: v.name || '' }))
                    .sort((a, b) => a.name.localeCompare(b.name))
        } catch (e) {
            console.warn('loadVenues:', e)
            this.venues = []
            this.$store.toastr.error('Failed to load venues')
        }
    },

    async saveBasics(showAlert = true) {
        this.saving.basics = true
        try {
            if (!this.form.name?.trim()) throw new Error('Team name is required.')

            const payload = {
                name: this.form.name.trim(),
                active: !!this.form.active,
                photoUrl: this.form.photoUrl || null,
                logoUrl: this.form.logoUrl || null,
                captainPlayerId: this.form.captainPlayerId || null,
                venueId: this.form.venueId || null,
                league: String(this.form.league || '').trim(),
            }

            if (this.isEditing) {
                const r = await apiFetch(`/teams/${this.form._id}`, { method: 'PATCH', body: payload })
                const d = await r.json().catch(() => ({}))
                if (!r.ok) throw new Error(d.message || 'Save failed')
                if (showAlert) {
                    this.$store.toastr.success('Team updated');
                }
            } else {
                const created = await apiFetch('/teams', { method: 'POST', body: payload })
                const data = await created.json().catch(() => ({}))
                if (!created.ok) {
                    this.$store.toastr.error(data.message || 'Create failed')
                    return
                }
                this.$store.toastr.success('Team created')
                const row = unwrapApiEntity(data, ['team', 'item'])
                const newId = entityDocId(row)
                if (newId) {
                    location.href = `/admin/teams/${encodeURIComponent(newId)}/edit`
                }
            }
        } catch (e) {
            this.$store.toastr.warning(e.message || 'Save failed')
        } finally {
            this.saving.basics = false
        }
    },

    /* ---------- Search & Add ---------- */
    pruneAssignSearchSelection(items) {
        const next = { ...this.builderSearchSelected }
        for (const k of Object.keys(next)) {
            const still = items.some((p) => this.id(p._id) === k && this.canAssign(p))
            if (!still) delete next[k]
        }
        this.builderSearchSelected = next
    },

    async loadPlayerAssignPool() {
        if (!this.isEditing) return
        this.busy.search = true
        try {
            const r = await apiFetch(
                `/players?limit=20&sortBy=createdAt:desc&select=${PLAYER_ASSIGN_SELECT}`,
            )
            const d = await r.json()
            if (!r.ok) throw new Error(d.message || 'Load failed')
            const items = Array.isArray(d.items) ? d.items : []
            this.search.results = items
            this.search.open = true
            this.pruneAssignSearchSelection(items)
        } catch (e) {
            console.warn('loadPlayerAssignPool:', e)
            this.$store.toastr.error('Could not load players')
        } finally {
            this.busy.search = false
        }
    },

    closePlayerSearch() {
        this.search.open = false
        this.clearBuilderSearchSelection()
    },

    async searchPlayers() {
        const q = this.search.q.trim()
        if (!this.isEditing) return
        if (!q) {
            await this.loadPlayerAssignPool()
            return
        }
        this.busy.search = true
        try {
            const r = await apiFetch(
                `/players?q=${encodeURIComponent(q)}&limit=30&sortBy=createdAt:desc&select=${PLAYER_ASSIGN_SELECT}`,
            )
            const d = await r.json()
            if (!r.ok) throw new Error(d.message || 'Search failed')

            const items = Array.isArray(d.items) ? d.items : Array.isArray(d) ? d : []
            this.search.results = items
            this.search.open = true
            this.pruneAssignSearchSelection(items)
        } catch (e) {
            console.warn('searchPlayers:', e)
            this.search.open = false
            this.$store.toastr.error('Player search failed')
        } finally {
            this.busy.search = false
        }
    },

    updatePlayer(id, patch) {
        return (async () => {
            const r = await apiFetch(`/players/${id}/profile`, { method: 'PATCH', body: patch });
            const d = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(d.message || 'Player update failed');
            return d;
        })();
    },

    async makeCaptain(p) {
        if (this.isCaptain(p)) {
            this.$store.toastr.info('Already captain')
            return
        }
        this.setBusy('captain', p._id, true);
        const newCapId = this.id(p._id);
        const prevCapId = this.id(this.form.captainPlayerId || '');

        try {
            this.form.captainPlayerId = newCapId;
            await this.saveBasics(false);

            // 2) Flip player flags
            if (prevCapId && prevCapId !== newCapId) {
                // await this.refreshIfSelf(prevCapId);
                await Alpine.store('session').refreshIfSelf(prevCapId);
            }

            // Ensure the new captain is active & tied to the team
            await this.updatePlayer(newCapId, {
                status: 'active',
                registeredTeamId: this.teamId
            });

            // await this.refreshIfSelf(newCapId);
            await Alpine.store('session').refreshIfSelf(newCapId);
            // await this.loadRoster(this.teamId);
            this.$store.toastr.success('Captain updated');
        } catch (e) {
            this.$store.toastr.error(e.message || 'Failed to set captain')
        } finally {
            this.setBusy('captain', p._id, false)
        }
    },


    /**
     * Assign a player to the team.
     * kind: 'roster' | 'reserve'
     * We ALWAYS add to team.playerIds (type 'roster'), then set Player.status accordingly.
     */
    async assignPlayer(p, kind, opts = {}) {
        const silent = !!opts.silent
        const reload = opts.reload !== false
        const force = !!opts.force
        const strict = !!opts.strict
        const keepSearchOpen = opts.closeSearch !== true
        if (!force && !this.canAssign(p)) {
            if (strict) throw new Error('Player cannot be assigned to this team')
            return false
        }
        if (!this.teamId || !p?._id) {
            if (strict) throw new Error('Team or player is missing')
            return false
        }
        this.setBusy('assign', p._id, true)
        try {
            // 1) add to team roster
            const r1 = await apiFetch(`/teams/${this.teamId}/roster`, {
                method: 'POST',
                body: {
                    playerId: p._id,
                    type: kind === 'reserve' ? 'reserve' : kind === 'inactive' ? 'inactive' : 'roster',
                },
            })
            const d1 = await r1.json()
            if (!r1.ok) throw new Error(d1.message || 'Add failed')

            // 2) set player status + registeredTeamId (roster POST already persists; keep profile in sync)
            const status = kind === 'reserve' ? 'reserve' : kind === 'inactive' ? 'inactive' : 'active'
            await this.updatePlayer(p._id, { status, registeredTeamId: this.teamId });

            await Alpine.store('session').refreshIfSelf(p._id);

            this.teamPlayerIds.add(this.id(p._id))
            p.registeredTeamId = this.teamId

            if (keepSearchOpen) {
                const pid = this.id(p._id)
                this.search.results = (this.search.results || []).filter((x) => this.id(x._id) !== pid)
                if (this.builderSearchSelected[pid]) {
                    const next = { ...this.builderSearchSelected }
                    delete next[pid]
                    this.builderSearchSelected = next
                }
            } else {
                this.search.q = ''
                this.search.results = []
                this.search.open = false
                this.clearBuilderSearchSelection()
            }

            if (!silent) {
                const msg = kind === 'reserve' ? 'Added as reserve' : kind === 'inactive' ? 'Added as inactive' : 'Added to match squad'
                this.$store.toastr.success(msg)
            }
            if (reload) await this.loadRoster(this.teamId)
            return true
        } catch (e) {
            if (strict) throw e
            this.$store.toastr.error(e.message || 'Add failed')
            return false
        } finally {
            this.setBusy('assign', p._id, false)
        }
    },

    /* ---------- Move between squad / inactive / reserve ---------- */
    async setPlayerRole(p, role) {
        const target = String(role || 'active').toLowerCase()
        if (this.isCaptain(p) && target !== 'active') {
            this.$store.toastr.info('Assign a new captain before changing this player\'s role')
            return
        }
        this.setBusy('move', p._id, true)
        try {
            await this.updatePlayer(p._id, { status: target, registeredTeamId: this.teamId })
            await Alpine.store('session').refreshIfSelf(p._id)
            const labels = {
                active: 'Moved to match squad',
                inactive: 'Player deactivated — still on team, hidden from lineups',
                reserve: 'Moved to reserves',
            }
            this.$store.toastr.success(labels[target] || 'Player updated')
            await this.loadRoster(this.teamId)
        } catch (e) {
            this.$store.toastr.error(e.message || 'Update failed')
        } finally {
            this.setBusy('move', p._id, false)
        }
    },

    async moveTo(target, p) {
        const map = { roster: 'active', squad: 'active', active: 'active', inactive: 'inactive', reserve: 'reserve' }
        await this.setPlayerRole(p, map[target] || target)
    },

    /* ---------- Remove Player from Team ---------- */
    confirmRemove(p) {
        if (this.isCaptain(p)) { this.$store.toastr.info('Captain cannot be removed'); return; }
        this.toRemovePlayer = p;
        this.removeOpen = true;
    },

    async doRemovePlayer() {
        if (!this.toRemovePlayer) return;
        const p = this.toRemovePlayer;
        this.removeBusy = true;
        this.setBusy('remove', p._id, true);

        try {
            // 1) Remove from team roster list
            const r = await apiFetch(`/teams/${this.teamId}/roster`, {
                method: 'DELETE',
                body: { playerId: p._id, type: 'roster' }
            });
            const d = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(d.message || 'Remove failed');

            // 2) If captain, clear team captain + player flag
            if (this.form.captainPlayerId === this.id(p._id)) {
                this.form.captainPlayerId = '';
                await this.saveBasics(false);
            }

            // 3) Detach player from team; choose your default status when unaffiliated
            await this.updatePlayer(p._id, { registeredTeamId: null, status: 'pool' });

            // 4) Refresh session if self
            // await this.refreshIfSelf(p._id);
            await Alpine.store('session').refreshIfSelf(p._id);

            this.$store.toastr.success('Player removed');
            this.removeOpen = false;
            this.toRemovePlayer = null;
            await this.loadRoster(this.teamId);
        } catch (e) {
            this.$store.toastr.error(e.message || 'Remove failed');
        } finally {
            this.removeBusy = false;
            this.setBusy('remove', p._id, false);
        }
    },

    // Delete Team
    confirmDelete() { this.deleteOpen = true; },
    async doDelete() {
        if (!this.form._id || this.deleteBusy) return;
        this.deleteBusy = true;
        try {
            const res = await apiFetch(`/teams/${this.form._id}`, { method: 'DELETE' });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data?.message || 'Delete failed');

            this.$store.toastr.success(`Team ${this.form.name} deleted`);
            this.deleteOpen = false;
            this.deleteBusy = false;
            location.href = '/admin/teams';

        } catch (e) {
            this.$store.toastr.error(e.message || 'Delete failed');
            this.deleteOpen = false;
        } finally {
            this.deleteBusy = false;
        }
    },
}))
